import { useState, useEffect, useRef, useMemo, useCallback, type RefObject } from 'react';
import type {
  LibEmptyVariant,
  LibPlanSortKey,
  WorkoutPlanDurationBucket,
} from '../types/library';
import { PLAN_DURATION_BUCKET_LABELS, VALID_PLAN_DURATION_BUCKETS } from '../types/library';

export const LIB_WORKOUT_PLANS_PAGE_SIZE = 15;

export const LIB_PLAN_SORT_OPTIONS: { id: LibPlanSortKey; label: string }[] = [
  { id: 'newest', label: 'Newest first' },
  { id: 'oldest', label: 'Oldest first' },
  { id: 'name-asc', label: 'Name (A→Z)' },
  { id: 'name-desc', label: 'Name (Z→A)' },
  { id: 'duration-desc', label: 'Longest duration' },
  { id: 'duration-asc', label: 'Shortest duration' },
  { id: 'most-wk', label: 'Most workouts' },
  { id: 'fewest-wk', label: 'Fewest workouts' },
];

export type UseLibraryWorkoutPlansParams = {
  workoutPlans: any[];
};

export function useLibraryWorkoutPlans({ workoutPlans }: UseLibraryWorkoutPlansParams) {
  const [workoutPlanNameFilter, setWorkoutPlanNameFilter] = useState('');
  const [libPlanDurationBuckets, setLibPlanDurationBuckets] = useState<Set<WorkoutPlanDurationBucket>>(new Set());
  const [libPlanSort, setLibPlanSort] = useState<LibPlanSortKey>('newest');
  const [libPlanSortExplicit, setLibPlanSortExplicit] = useState(false);
  const [libPlansPage, setLibPlansPage] = useState<number>(1);
  const [libPlanOpenChip, setLibPlanOpenChip] = useState<'duration' | 'sort' | null>(null);
  const libPlanDurationChipRef = useRef<HTMLDivElement>(null);
  const libPlanSortChipRef = useRef<HTMLDivElement>(null);

  const planWeeksOf = useCallback((plan: any): number | null => {
    const days = plan?.totalNoOfDays;
    if (typeof days !== 'number' || !Number.isFinite(days) || days <= 0) return null;
    return Math.round(days / 7);
  }, []);

  const libPlanDurationActive = libPlanDurationBuckets.size > 0;

  const planWeeksMatchesBucket = useCallback((weeks: number, bucket: WorkoutPlanDurationBucket): boolean => {
    if (bucket === '1-4') return weeks >= 1 && weeks <= 4;
    if (bucket === '5-8') return weeks >= 5 && weeks <= 8;
    if (bucket === '9-12') return weeks >= 9 && weeks <= 12;
    return weeks >= 13;
  }, []);

  const filteredAndSortedWorkoutPlans = useMemo<any[]>(() => {
    const q = workoutPlanNameFilter.trim().toLowerCase();
    const filtered = workoutPlans.filter((plan: any) => {
      if (q && !(plan.name || '').toLowerCase().includes(q)) return false;

      if (libPlanDurationBuckets.size > 0) {
        const weeks = planWeeksOf(plan);
        if (weeks === null) return false;
        const buckets = Array.from(libPlanDurationBuckets);
        if (!buckets.some((b) => planWeeksMatchesBucket(weeks, b))) return false;
      }

      return true;
    });

    const cmpName = (a: any, b: any) =>
      String(a.name || '').toLowerCase().localeCompare(String(b.name || '').toLowerCase());
    const cmpCreated = (a: any, b: any) =>
      (a.createdAt ? new Date(a.createdAt).getTime() : 0) -
      (b.createdAt ? new Date(b.createdAt).getTime() : 0);
    const cmpDuration = (a: any, b: any) => (a.totalNoOfDays ?? 0) - (b.totalNoOfDays ?? 0);
    const cmpWorkoutCount = (a: any, b: any) => (a.workoutCount ?? 0) - (b.workoutCount ?? 0);

    filtered.sort((a, b) => {
      switch (libPlanSort) {
        case 'newest':         return -cmpCreated(a, b);
        case 'oldest':         return cmpCreated(a, b);
        case 'name-asc':       return cmpName(a, b);
        case 'name-desc':      return -cmpName(a, b);
        case 'duration-desc':  return -cmpDuration(a, b);
        case 'duration-asc':   return cmpDuration(a, b);
        case 'most-wk':        return -cmpWorkoutCount(a, b);
        case 'fewest-wk':      return cmpWorkoutCount(a, b);
        default:               return 0;
      }
    });
    return filtered;
  }, [
    workoutPlans,
    workoutPlanNameFilter,
    libPlanDurationBuckets,
    libPlanSort,
    planWeeksOf,
    planWeeksMatchesBucket,
  ]);

  /** Mobile list uses name search only (legacy mobile path). */
  const filteredWorkoutPlansByName = useMemo(() => {
    if (!workoutPlanNameFilter.trim()) return workoutPlans;
    const q = workoutPlanNameFilter.toLowerCase();
    return workoutPlans.filter((p) => (p.name || '').toLowerCase().includes(q));
  }, [workoutPlans, workoutPlanNameFilter]);

  const planPassesWithoutDuration = useCallback(
    (plan: any): boolean => {
      const q = workoutPlanNameFilter.trim().toLowerCase();
      if (q && !(plan.name || '').toLowerCase().includes(q)) return false;
      return true;
    },
    [workoutPlanNameFilter],
  );

  const libPlanDurationMatchCounts = useMemo<Record<WorkoutPlanDurationBucket, number>>(() => {
    const out: Record<WorkoutPlanDurationBucket, number> = { '1-4': 0, '5-8': 0, '9-12': 0, '13+': 0 };
    for (const p of workoutPlans) {
      if (!planPassesWithoutDuration(p)) continue;
      const w = planWeeksOf(p);
      if (w === null) continue;
      if (w <= 4) out['1-4']++;
      else if (w <= 8) out['5-8']++;
      else if (w <= 12) out['9-12']++;
      else out['13+']++;
    }
    return out;
  }, [workoutPlans, planPassesWithoutDuration, planWeeksOf]);

  const libPlansHasActiveControls =
    libPlanDurationBuckets.size > 0
    || workoutPlanNameFilter.trim() !== ''
    || libPlanSortExplicit
    || libPlanSort !== 'newest';

  const libPlansAppliedCount =
    libPlanDurationBuckets.size
    + (workoutPlanNameFilter.trim() !== '' ? 1 : 0)
    + (libPlanSortExplicit || libPlanSort !== 'newest' ? 1 : 0);

  const handleClearAllPlans = useCallback(() => {
    setLibPlanDurationBuckets(new Set());
    setWorkoutPlanNameFilter('');
    setLibPlanSort('newest');
    setLibPlanSortExplicit(false);
    setLibPlansPage(1);
  }, []);

  const libPlansTotalPages = Math.max(1, Math.ceil(filteredAndSortedWorkoutPlans.length / LIB_WORKOUT_PLANS_PAGE_SIZE));

  const libPlansPageRows = useMemo<any[]>(() => {
    const start = (libPlansPage - 1) * LIB_WORKOUT_PLANS_PAGE_SIZE;
    return filteredAndSortedWorkoutPlans.slice(start, start + LIB_WORKOUT_PLANS_PAGE_SIZE);
  }, [filteredAndSortedWorkoutPlans, libPlansPage]);

  const libPlansRangeStart =
    filteredAndSortedWorkoutPlans.length === 0 ? 0 : (libPlansPage - 1) * LIB_WORKOUT_PLANS_PAGE_SIZE + 1;
  const libPlansRangeEnd = Math.min(
    filteredAndSortedWorkoutPlans.length,
    libPlansPage * LIB_WORKOUT_PLANS_PAGE_SIZE,
  );

  useEffect(() => {
    setLibPlansPage(1);
  }, [workoutPlanNameFilter, libPlanDurationBuckets, libPlanSort, workoutPlans.length]);

  useEffect(() => {
    if (libPlansPage > libPlansTotalPages) setLibPlansPage(libPlansTotalPages);
  }, [libPlansPage, libPlansTotalPages]);

  useEffect(() => {
    if (!libPlanOpenChip) return;
    const handle = (e: Event) => {
      const target = e.target as Node;
      const refs: [typeof libPlanOpenChip, RefObject<HTMLDivElement | null>][] = [
        ['duration', libPlanDurationChipRef],
        ['sort', libPlanSortChipRef],
      ];
      for (const [name, ref] of refs) {
        if (libPlanOpenChip === name && ref.current && !ref.current.contains(target)) {
          setLibPlanOpenChip(null);
        }
      }
    };
    document.addEventListener('mousedown', handle);
    document.addEventListener('touchstart', handle);
    return () => {
      document.removeEventListener('mousedown', handle);
      document.removeEventListener('touchstart', handle);
    };
  }, [libPlanOpenChip]);

  const handleLibPlanHeaderSort = useCallback((column: 'name' | 'createdAt') => {
    setLibPlanSortExplicit(true);
    if (column === 'name') {
      setLibPlanSort((curr) => (curr === 'name-asc' ? 'name-desc' : 'name-asc'));
    } else {
      setLibPlanSort((curr) => (curr === 'newest' ? 'oldest' : 'newest'));
    }
  }, []);

  const handleLibPlanDurationHeaderSort = useCallback(() => {
    setLibPlanSortExplicit(true);
    setLibPlanSort((curr) => (curr === 'duration-desc' ? 'duration-asc' : 'duration-desc'));
  }, []);

  const libPlanSortIsDefault = libPlanSort === 'newest';
  const libPlanSortShowsSelection = libPlanSortExplicit || !libPlanSortIsDefault;
  const libPlanCurrentSortLabel = LIB_PLAN_SORT_OPTIONS.find((o) => o.id === libPlanSort)?.label ?? 'Newest first';

  const libPlansEmptyVariant: LibEmptyVariant = (() => {
    if (workoutPlans.length === 0) return 'true-empty';
    if (filteredAndSortedWorkoutPlans.length === 0 && libPlansHasActiveControls) return 'no-match';
    return null;
  })();

  const libBuildPageWindow = useCallback((current: number, total: number): Array<number | 'ellipsis'> => {
    if (total <= 5) return Array.from({ length: total }, (_, i) => i + 1);
    if (current <= 3) return [1, 2, 3, 4, 'ellipsis', total];
    if (current >= total - 2) return [1, 'ellipsis', total - 3, total - 2, total - 1, total];
    return [1, 'ellipsis', current - 1, current, current + 1, 'ellipsis', total];
  }, []);

  return {
    workoutPlanNameFilter,
    setWorkoutPlanNameFilter,
    libPlanDurationBuckets,
    setLibPlanDurationBuckets,
    libPlanSort,
    setLibPlanSort,
    libPlanSortExplicit,
    setLibPlanSortExplicit,
    libPlansPage,
    setLibPlansPage,
    libPlanOpenChip,
    setLibPlanOpenChip,
    libPlanDurationChipRef,
    libPlanSortChipRef,
    planWeeksOf,
    libPlanDurationActive,
    libPlanDurationMatchCounts,
    filteredAndSortedWorkoutPlans,
    filteredWorkoutPlansByName,
    libPlansHasActiveControls,
    libPlansAppliedCount,
    handleClearAllPlans,
    libPlansTotalPages,
    libPlansPageRows,
    libPlansRangeStart,
    libPlansRangeEnd,
    handleLibPlanHeaderSort,
    handleLibPlanDurationHeaderSort,
    libPlanSortIsDefault,
    libPlanSortShowsSelection,
    libPlanCurrentSortLabel,
    libPlansEmptyVariant,
    libBuildPageWindow,
  };
}

export type LibraryWorkoutPlansApi = ReturnType<typeof useLibraryWorkoutPlans>;
